/**
 * Helper utility for iframe / popup embed communication in Payloft Gateway.
 * Supports secure, cross-window postMessage communication with the parent SDK / merchant site.
 */

export const isEmbedMode = () => {
  if (typeof window === "undefined") return false;
  try {
    const params = new URLSearchParams(window.location.search);
    return (
      params.get("embed") === "true" ||
      window.self !== window.top
    );
  } catch (e) {
    return false;
  }
};

export const getTargetOrigin = () => {
  if (typeof window === "undefined") return "*";
  try {
    const params = new URLSearchParams(window.location.search);
    const parentOrigin = params.get("parentOrigin");
    if (parentOrigin && (parentOrigin.startsWith("http://") || parentOrigin.startsWith("https://"))) {
      return new URL(parentOrigin).origin;
    }
    return "*";
  } catch (e) {
    return "*";
  }
};

/**
 * Sanitizes payload before sending to parent window to prevent accidental leakage of sensitive payment details.
 */
const sanitizePayload = (payload) => {
  if (!payload || typeof payload !== "object") return payload || {};
  const sanitized = { ...payload };

  // Never expose sensitive fields to parent window
  const sensitiveKeys = ["pan", "cardNumber", "cvv", "pin", "otp", "password", "token"];
  sensitiveKeys.forEach((key) => {
    delete sanitized[key];
  });

  return sanitized;
};

export const postParentMessage = (type, payload = {}) => {
  if (typeof window === "undefined" || !window.parent || window.parent === window) {
    return;
  }

  const targetOrigin = getTargetOrigin();
  const message = {
    source: "payloft-gateway",
    type,
    payload: sanitizePayload(payload),
    timestamp: Date.now(),
  };

  try {
    window.parent.postMessage(message, targetOrigin);
  } catch (err) {
    console.error("[Payloft Embed] postMessage error:", err);
  }
};

export const notifyReady = () => {
  postParentMessage("pay:ready", {});
};

export const notifySuccess = (data = {}) => {
  postParentMessage("pay:success", data);
};

export const notifyClose = () => {
  postParentMessage("pay:close", {});
};

export const notifyError = (error = {}) => {
  postParentMessage("pay:error", error);
};

export const notifyRedirecting = (data = {}) => {
  postParentMessage("pay:redirecting", data);
};
